import React, { useEffect, useState } from "react";
import { useSelector, reportsActions, s3Actions } from "inbooma-api";
import { Biscuit, getAllUrlParams, Notification } from "../../../../utils";
import t from "../../../helpers/t";
import InboomaSubmenu from "../../../components/InboomaSubmenu";
import SortNav from "../../../components/SortNav";
import moment from "moment";
import ReportsList from "./ReportsList";
import { getApiHost, getEnv } from "../../../../Sync";
import { _ } from "../../../../../vendor";
import * as FileSaver from "../../../../../vendor/FileSaver";
import ReportsSidebar from "./ReportsSidebar";
import AddReport from "./AddReport";

type Props = {
  name: string;
  dispatch: any;
  returnPath: string;
  params: any;
  intAdd: boolean;
};

export type Filter = {
  page?: number | string;
  sort?: string;
  direction?: string;
  limit?: number;
};

export type DriveReport = {
  client_id: number;
  filename?: string;
  begin: string;
  end: string;
  id?: number;
  created?: string | null;
  modified?: string | null;
  deleted?: string | null;
  created_by?: string | null;
  modified_by?: string | null;
  environment?: string | null;
  change_logs?: any;
};

const ReportsView = (props: Props) => {
  // url params
  const currentUrl = new URL(window.location.href);
  const searchParams = currentUrl.searchParams;
  const allUrlParams: any = getAllUrlParams(window.location.href);
  const allUrlParamsClone = { ...allUrlParams };
  const language: string = Biscuit.get("user.lang").replace("-", "_");
  const paginationLimit: number = 20;
  let searchOption = null;
  if (allUrlParams?.date !== undefined) {
    searchOption = "date";
  }
  const _queryState = {
    timeStamp: 0,
    value: "",
  };

  // const for props
  const { name, dispatch, returnPath, params, intAdd } = props;

  // selectors
  const _reports = useSelector((state: any) => state.reports_data.getReports);
  const _addReport = useSelector((state: any) => state.reports_data.addReport);
  const _deleteReport = useSelector((state: any) => state.reports_data.deleteReport);
  const _getReport = useSelector((state: any) => state.s3_data.getDocumentS3);

  // states
  const [initialized, setInitialized] = useState(false);
  const [reportsPending, setReportsPending] = useState(false);
  const [reportPending, setReportPending] = useState(false);
  const [getReportPending, setGetReportPending] = useState(false);
  const [add, setAdd] = useState(intAdd);
  const [pagination, setPagination] = useState({
    has_next_page: false,
    current_page: 1,
    count: 1,
    page_count: 1,
    has_prev_page: false,
    limit: paginationLimit,
  });
  const [sort, setSort] = useState({
    sort: "created",
    direction: "desc",
  });
  const [filter, setFilter] = useState(function (): any {
    let f: { page: number } = { page: 1 };
    if (allUrlParams?.page !== undefined) {
      f.page = allUrlParams?.page;
    }
    return f;
  });
  const [searchbarOptionsSelected, setSearchBarOptionsSelected] = useState(searchOption);

  const getReports = () => {
    let requestFilter = { ...filter };
    requestFilter.sort = sort.sort;
    requestFilter.direction = sort.direction;
    requestFilter.limit = paginationLimit;
    requestFilter.page = 1;
    dispatch(
      reportsActions.getReports({
        token: Biscuit.get("token"),
        filter: requestFilter,
        parameters: {
          headers: {
            "Accept-Language": language,
            "Content-Language": language,
          },
        },
      }),
    );
    setReportsPending(true);
  };

  // useEffects
  // intialize
  useEffect(() => {
    if (!initialized && !reportsPending) {
      getReports();
      setInitialized(true);
    }
  }, []);

  // download xlsx
  useEffect(() => {
    if (_getReport.success === "" || !getReportPending) {
      return;
    }
    if (_getReport.success === true) {
      const blob = new Blob([_getReport.data], { type: "application/excel" });
      // @ts-ignore
      FileSaver.saveAs(blob, getReportPending.replace(/_/gi, " "));
    } else {
      alert("Could not download: " + _getReport.data);
    }
    setGetReportPending(false);
  }, [_getReport]);

  // reports (Index)
  useEffect((): void => {
    if (_reports.success === "" || !reportsPending) {
      return;
    }
    if (_reports.success !== true) {
      console.log("error loading reports");
    } else {
      setPagination(_reports.pagination);
    }
    setReportsPending(false);
  }, [_reports]);

  useEffect((): void => {
    if (_addReport.success === "" || !reportPending) {
      return;
    }
    if (_addReport.success === true) {
      setReportPending(false);
      setAdd(false);
      window.location.replace("/reports");
    } else {
      showNotification(
        "warning",
        null,
        "Fahrbericht konnte nicht gespeichert werden. API Message: " + _addReport.data.message,
      );
    }
    setReportPending(false);
  }, [_addReport]);

  useEffect(() => {
    if (_deleteReport.success === "" || !reportPending) {
      return;
    }
    if (_deleteReport.success === true) {
      setReportPending(false);
      getReports();
      //window.location.replace("/reports");
    } else {
      showNotification(
        "warning",
        null,
        "Fahrbericht konnte nicht gelöscht werden. API Message: " + _deleteReport.data.message,
      );
      setReportPending(false);
    }
  }, [_deleteReport]);

  // Pagination
  const loadNextPage = (page: number) => {
    setReportsPending(true);

    let requestFilter = { ...filter };
    requestFilter.sort = sort.sort;
    requestFilter.direction = sort.direction;
    requestFilter.limit = paginationLimit;
    requestFilter.page = page;
    if (page > 1) {
      searchParams.set("page", page.toString());
      const newRelativePathQuery = window.location.pathname + "?" + searchParams.toString();
      history.pushState(null, "", newRelativePathQuery);
    } else {
      searchParams.delete("page");
      const newRelativePathQuery = window.location.pathname + "?" + searchParams.toString();
      history.pushState(null, "", newRelativePathQuery);
    }
    // fetch next page
    props.dispatch(
      reportsActions.getReports({
        token: Biscuit.get("token"),
        suffix: "",
        filter: requestFilter,
        parameters: {
          headers: {
            "Accept-Language": language,
            "Content-Language": language,
          },
        },
      }),
    );
  };

  // filter and sort change
  const filterChangedHandle = (filter: any, sorts = null) => {
    let requestFilter = { ...filter };
    if (sorts === null) {
      // @ts-ignore
      sorts = sort;
    } else {
      setSort(sorts);
    }
    // @ts-ignore
    requestFilter.sort = sorts.sort;
    // @ts-ignore
    requestFilter.direction = sorts.direction;
    if (typeof filter.limit !== "undefined") {
      requestFilter.limit = filter.limit;
      if (typeof requestFilter.page !== "undefined") {
        delete requestFilter.page;
      }
    } else {
      requestFilter.limit = paginationLimit;
      requestFilter.page = 1;
    }
    if (typeof filter.find === "undefined") {
      requestFilter.find = "WithCoordinates";
    }

    props.dispatch(
      reportsActions.getReports({
        token: Biscuit.get("token"),
        suffix: "",
        filter: requestFilter,
        parameters: {
          headers: {
            "Accept-Language": language,
            "Content-Language": language,
          },
        },
      }),
    );
    setReportsPending(true);
    setFilter(filter);
  };

  const onSortChangedHandle = (ev: any) => {
    const el = ev.currentTarget;
    const sortKey = el.getAttribute("data-inb-val"),
      direction = el.getAttribute("data-inb-direction"),
      isOppositeElement = direction === null && el.classList.contains("is-active"),
      newDirection = direction === "desc" || isOppositeElement ? "asc" : "desc";

    if (sortKey) {
      let requestFilter;
      if (typeof filter.limit !== "undefined") {
        requestFilter = {
          ...filter,
          sort: sortKey,
          direction: direction,
        };
      } else {
        requestFilter = {
          ...filter,
          sort: sortKey,
          direction: direction,
          limit: paginationLimit,
          page: 1,
        };
      }
      props.dispatch(
        reportsActions.getReports({
          token: Biscuit.get("token"),
          suffix: "",
          filter: requestFilter,
          parameters: {
            headers: {
              "Accept-Language": language,
              "Content-Language": language,
            },
          },
        }),
      );
      setReportsPending(true);
      setSort({
        sort: sortKey,
        direction: direction,
      });

      const activeSortTab = $(".is-active");
      _.each(activeSortTab, function (key, value) {
        key.classList.remove("is-active");
      });
      el.classList.add("is-active");
      el.setAttribute("data-inb-direction", isOppositeElement ? "desc" : newDirection);
    }
  };

  // show Notifications
  const showNotification = (
    msgType: string,
    itemId: number | string | null,
    apiMsg: string = "",
    type: string = "report",
  ): void => {
    // little delay necessary to make notifications visible (render of background must be done before)
    setTimeout(() => {
      if (msgType === "success") {
        new Notification({
          message:
            apiMsg !== "" ? apiMsg : t("saved_${a}", { a: t(type) }, "capital") + " #" + itemId,
          duration: 5000,
          type: "success",
          closeable: true,
          icon: "fi fi-check",
        });
      } else if (msgType === "warning") {
        new Notification({
          message:
            typeof apiMsg !== "undefined" && apiMsg !== null
              ? t(apiMsg, null, "capital")
              : t("request_error", null, "capital"),
          duration: 5000,
          type: "warning",
          closeable: true,
          icon: "fi fi-x",
        });
      }
    }, 200);
  };

  const saveReport = (report: Report): void => {
    dispatch(
      reportsActions.addReport({
        token: Biscuit.get("token"),
        body: report,
        parameters: {
          headers: {
            "Accept-Language": language,
            "Content-Language": language,
          },
        },
      }),
    );
    setReportPending(true);
  };
  // Searchbar Handler
  const searchBarHandler = (ev: any) => {
    let query: string;
    if (typeof ev.target !== "undefined") {
      query = ev.target.value.toLowerCase();
    } else {
      if (typeof ev === "string") {
        query = ev;
      } else {
        query = "";
      }
    }
    searchParams.delete("date");
    allUrlParams?.date !== undefined && delete allUrlParams.date;
    if (query !== "") {
      if (searchbarOptionsSelected !== null) {
        searchParams.set(searchbarOptionsSelected, query);
      }
      searchParams.set("date", query);
    }
    const newRelativePathQuery = window.location.pathname + "?" + searchParams.toString();
    history.pushState(null, "", newRelativePathQuery);
    let standardFilter = {
      sort: "date",
      direction: "desc",
      page: 1,
      limit: 40,
      ...allUrlParams,
    };
    const minLength = 1;
    let filterA = standardFilter;
    if (query.length >= minLength && searchbarOptionsSelected === null) {
      // work user search only for values bigger or equal to the minimum length

      setTimeout(function () {
        let diff = Number(moment().format("x")) - _queryState.timeStamp;

        if (diff > 700 && _queryState.value === query) {
          // no changes after the last 700 ms -> user finished input -> start request
          filterChangedHandle({
            ...filterA,
            date: query,
          });
        }
      }, 800);
    } else if (query.length >= minLength && searchbarOptionsSelected !== null) {
      let cloneA: any = {};
      Object.assign(cloneA, filterA);
      cloneA[searchbarOptionsSelected] = query;
      filterChangedHandle(cloneA);
    } else {
      filterChangedHandle(filterA);
    }
    _queryState.timeStamp = Number(moment().format("x"));
    _queryState.value = query;
  };

  const tabs: (
    | {
        name: any;
        mediumWidth: number;
        smallWidth: number;
        title: any;
        canSort: boolean;
        extraClasses: string;
        sort: { direction: string; data_inb_val: string };
        preselected?: boolean;
      }
    | {
        name: any;
        mediumWidth: number;
        smallWidth: number;
        title: any;
        canSort: boolean;
        extraClasses: string;
        sort?: { direction: null; data_inb_val: string };
      }
  )[] = [
    {
      name: t("date"),
      mediumWidth: 2,
      smallWidth: 4,
      title: t("sort_by_${a}", { a: t("date") }),
      canSort: true,
      extraClasses: "inb-order-big padding-1",
      preselected: sort.sort === "date",
      sort: {
        direction: null,
        data_inb_val: "date",
      },
    },
    {
      name: t("name"),
      mediumWidth: 2,
      smallWidth: 4,
      title: t("name"),
      canSort: false,
      extraClasses: "inb-order-big padding-1",
    },
    {
      name: t("created"),
      mediumWidth: 2,
      smallWidth: 4,
      title: t("created"),
      canSort: true,
      sort: {
        direction: "desc",
        data_inb_val: "created",
      },
      preselected: sort.sort === "created",
      extraClasses: "inb-order-big padding-1",
    },
  ];

  const deleteReport: (report: DriveReport) => void = (report: DriveReport): void => {
    if (confirm(t("really_remove"))) {
      dispatch(
        reportsActions.deleteReport({
          token: Biscuit.get("token"),
          id: report.id,
          parameters: {
            headers: {
              "Accept-Language": language,
              "Content-Language": language,
            },
          },
        }),
      );
      // @ts-ignore
      setReportPending(report.id);
    }
  };

  const getReport: (report: DriveReport) => void = (report: DriveReport): void => {
    dispatch(
      s3Actions.getDocumentS3({
        client_id: Biscuit.get("client.id"),
        type: "Report",
        location: 0,
        environment: getEnv(true),
        name: report.filename,
        apiEntryPoint: getApiHost(),
        token: Biscuit.get("token"),
      }),
    );
    // @ts-ignore
    setGetReportPending(report.filename);
  };

  return (
    <>
      {initialized && <ReportsSidebar heading={t("reports", null, "capital")} setAdd={setAdd} />}
      <div id="inb-c-main" className="columns resource">
        <InboomaSubmenu
          searchbar={false}
          searchbarPlaceholder={t("date")}
          searchBarOptionsSelected={searchbarOptionsSelected}
          setSearchBarOptionsSelected={setSearchBarOptionsSelected}
          extraClasses={"sticky-top" + (paginationLimit < pagination.count) ? " hide" : ""}
          searchbarValue={allUrlParamsClone?.q !== undefined ? allUrlParamsClone.q : ""}
          searchbarHandler={searchBarHandler}
          smallOnlyTitle={t("reports", null, "capital")}
          pagination={pagination}
          itemsCountPerPage={paginationLimit}
          pageRangeDisplayed={5}
          showPagination={paginationLimit < pagination.count}
          onChange={loadNextPage}
          itemLength={_reports?.data?.length}
          tabs={[]}
        />

        <SortNav
          tabs={tabs}
          extraId={"resource-nav"}
          extraClasses={"sticky-top-sortbar"}
          listStyle="tight"
          handler={onSortChangedHandle}
        />
        {/* {initialized && resources.length > 0 && !resourcesPending && typeof pagination !== 'undefined' && mapStatus === 1 &&
                    <Paginator
                    pagination={pagination}
                    itemsCountPerPage={paginationLimit}
                    pageRangeDisplayed={5}
                    onChange={loadNextPage}
                    upper={true}
                    />} */}
        {initialized && _reports?.data?.length > 0 && !reportsPending && !add && (
          <ReportsList
            reports={_reports?.data}
            deleteReport={deleteReport}
            getReport={getReport}
            language={language}
            reportPending={reportPending}
            getReportPending={getReportPending}
          />
        )}

        {initialized && _reports?.data?.length < 1 && !reportsPending && (
          <>
            <div id="inb-app-collection-empty" className="inb-announcement">
              <div className="inb-image-box">
                <img src="assets/img/no_data.svg" />
              </div>
              <h3>{t("app_msg_collection_empty")}</h3>
              <p>{t("app_msg_collection_empty_body")}</p>
            </div>
          </>
        )}
        {reportsPending && (
          <>
            {" "}
            <div id="inb-app-collection-empty" className="inb-announcement">
              <div className="inb-image-box">
                <img className="rollingLoader colorize-black" src="assets/img/rolling_loader.svg" />
              </div>
            </div>
          </>
        )}

        {add && !reportPending && (
          <AddReport requestPath={returnPath} setAdd={setAdd} saveReport={saveReport} />
        )}
      </div>
    </>
  );
};

export default ReportsView;
